
import { useMemo } from 'react';
import { useEnhancedAuthContext } from '@/context/auth/EnhancedAuthContext';
import { NavItem } from '../types';
import { UserRole } from '@/context/auth/types';
import { deriveResourceIdFromPath, defaultReadPermissionFromPath } from '@/services/auth/permissionResolver';

export const useNavigationFiltering = (navItems: NavItem[]) => {
  const { user, hasPermission, hasRole, permissionsLoaded, isLoadingPermissions } = useEnhancedAuthContext();

  const filteredNavItems = useMemo(() => {
    console.log(`🧭 Navigation filtering - User: ${user?.id}, Superuser: ${user?.is_superuser}, Loading: ${isLoadingPermissions}, Loaded: ${permissionsLoaded}`);
    console.log(`🔍 Navigation items to filter:`, navItems.length, navItems.map(item => ({ title: item.title, permissions: item.requiredPermissions, children: item.children?.length || 0 })));
    
    // No user = no items
    if (!user) {
      console.log('❌ No user, returning empty navigation');
      return [];
    }

    // Superusers see everything immediately - no permission checks needed
    if (user.is_superuser) {
      console.log('👑 Superuser detected, showing all navigation items (bypassing permission checks)');
      return navItems;
    }

    // For regular users, check permission loading state
    if (isLoadingPermissions || !permissionsLoaded) {
      console.log('⏳/⚠️ Permissions not ready, showing minimal navigation with unprotected children only');
      const minimal = navItems
        .map(item => {
          // Allow parent if it doesn't require explicit perms/roles
          if (!item.requiredPermissions?.length && !item.requiredRoles?.length) {
            let filteredChildren = item.children;
            if (item.children) {
              filteredChildren = item.children.filter(child =>
                !child.requiredPermissions?.length && !child.requiredRoles?.length
              );
              // If all children are protected, hide the parent during loading
              if (item.children.length > 0 && filteredChildren.length === 0) {
                console.log(`❌ Parent item hidden during loading: ${item.title} (no unprotected children)`);
                return null;
              }
            }
            return { ...item, children: filteredChildren };
          }
          return null;
        })
        .filter((i): i is NonNullable<typeof i> => i !== null);
      return minimal;
    }

    console.log('🔍 Filtering navigation based on loaded permissions');

    // Filter items based on loaded permissions
    const filterItem = (item: NavItem): NavItem | null => {
      // Determine context resource for this item
      const resource = deriveResourceIdFromPath(item.path);
      const context = resource ? { resource } : undefined;

      // Only check permissions if explicitly required on the item
      if (item.requiredPermissions?.length) {
        const hasExplicit = item.requiredPermissions.every(permission => {
          const result = hasPermission(permission, context);
          console.log(`🔍 Permission check: ${permission} (ctx: ${resource}) = ${result}`);
          return result;
        });

        let allowed = hasExplicit;

        // Fallback: allow default read for this path if explicit checks failed
        if (!allowed) {
          const fallbackPerm = defaultReadPermissionFromPath(item.path);
          if (fallbackPerm) {
            const fb = hasPermission(fallbackPerm, context);
            console.log(`↪️ Fallback permission check: ${fallbackPerm} (ctx: ${resource}) = ${fb}`);
            allowed = fb;
          }
        }

        if (!allowed) {
          console.log(`❌ Item filtered out: ${item.title} (missing permissions: ${item.requiredPermissions.join(', ')})`);
          return null;
        }
      }

      // Check roles if explicitly required on the item
      if (item.requiredRoles?.length) {
        const hasRequiredRole = hasRole(item.requiredRoles as UserRole[]);
        if (!hasRequiredRole) {
          console.log(`❌ Item filtered out: ${item.title} (missing roles: ${item.requiredRoles.join(', ')})`);
          return null;
        }
      }

      // Filter children recursively, regardless of whether the parent has requirements
      let filteredChildren = item.children;
      if (item.children) {
        filteredChildren = item.children
          .map(child => {
            const childResource = deriveResourceIdFromPath(child.path);
            const childContext = childResource ? { resource: childResource } : undefined;

            if (child.requiredPermissions?.length) {
              console.log(`🔍 Checking child: ${child.title}, permissions: ${child.requiredPermissions.join(', ')}`);
              const hasExplicitChild = child.requiredPermissions.every(p => {
                const result = hasPermission(p, childContext);
                console.log(`  - Permission ${p}: ${result}`);
                return result;
              });
              let allowedChild = hasExplicitChild;

              if (!allowedChild) {
                const fbPerm = defaultReadPermissionFromPath(child.path);
                if (fbPerm) {
                  const fbResult = hasPermission(fbPerm, childContext);
                  console.log(`  - Fallback permission ${fbPerm}: ${fbResult}`);
                  allowedChild = fbResult;
                }
              }

              if (!allowedChild) {
                console.log(`❌ Child filtered out: ${child.title}`);
                return null;
              }
              console.log(`✅ Child allowed: ${child.title}`);
            }
            if (child.requiredRoles?.length && !hasRole(child.requiredRoles as UserRole[])) {
              return null;
            }
            return child;
          })
          .filter((child): child is NonNullable<typeof child> => child !== null);

        // If all children filtered out, hide parent
        if (item.children.length > 0 && filteredChildren.length === 0) {
          console.log(`❌ Parent item filtered out: ${item.title} (no accessible children)`);
          return null;
        }
      }

      console.log(`✅ Item included: ${item.title}`);
      return { ...item, children: filteredChildren };
    };
    const result = navItems
      .map(filterItem)
      .filter((item): item is NonNullable<typeof item> => item !== null);
    
    console.log(`📋 Navigation filtered: ${navItems.length} → ${result.length} items`);
    return result;
  }, [navItems, user, hasPermission, hasRole, permissionsLoaded, isLoadingPermissions]);

  return filteredNavItems;
};
